// src/app/core/services/auth.service.ts
import { Injectable, inject } from '@angular/core';
import {
  Auth,
  AuthCredential,
  createUserWithEmailAndPassword,
  deleteUser,
  signInWithEmailAndPassword,
  signOut,
  authState,
  GoogleAuthProvider,
  FacebookAuthProvider,
  OAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  getAdditionalUserInfo,
  User,
  UserCredential,
  reauthenticateWithCredential,
  EmailAuthProvider,
  AuthProvider,
  linkWithCredential,
  signInWithCustomToken
} from '@angular/fire/auth';
import {
  Firestore,
  doc,
  setDoc,
  getDoc,
  serverTimestamp,
  Timestamp,
  docData
} from '@angular/fire/firestore';
import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { switchMap, distinctUntilChanged, map } from 'rxjs/operators';
import { NotificationService } from '@shared/services/notification.service';
import { UserModel } from '../models/user.model';
import { FirebaseError } from 'firebase/app';
import { FirebaseAuthError } from '@core/utils/firebase-error';
import { PROGRESS_STEPS } from '../constants/progress-steps.constants';
import { ThemeService } from '@core/services/theme.service';
import { Capacitor } from '@capacitor/core';

export type CurrentUserResult =
  | {
      status: 'authenticated';
      user: User;
    }
  | {
      status: 'unauthenticated';
      user: null;
    }
  | {
      status: 'error';
      user: null;
      error: unknown;
    };

export interface SessionConsistencyResult {
  isValid: boolean;
  reason?:
    | 'no-auth-user'
    | 'missing-firestore-user'
    | 'email-changed';
  uid?: string;
  oldEmail?: string | null;
  newEmail?: string | null;
}

export type SocialAuthIntent =
  | 'signin'
  | 'signup';

// =====================================================
// TIPO MODO SOCIAL
// -----------------------------------------------------
// - normal = Google/Facebook/Apple iniciado normalmente;
// - resolve-link = o usuário está autenticando a conta
// antiga para concluir uma vinculação que já está
// pendente.
// =====================================================
export type SocialAuthMode =
  | 'normal'
  | 'resolve-link';

// =====================================================
// ESTRATÉGIA DE EXECUÇÃO DO SOCIAL AUTH
// -----------------------------------------------------
// Define apenas COMO o fluxo OAuth será executado.
//
// - popup: Web desktop;
// - redirect: Web mobile / navegadores incompatíveis
//   com o fluxo popup;
// - native: aplicativo Capacitor iOS/Android.
//
// Não representa intenção de domínio (signin/signup)
// nem modo de vinculação (normal/resolve-link).
// =====================================================
export type SocialAuthExecutionStrategy =
  | 'popup'
  | 'redirect'
  | 'native';

// =====================================================
// CONTEXTO DE RETORNO DO SOCIAL AUTH VIA REDIRECT
// -----------------------------------------------------
// Mantém somente metadados não sensíveis necessários
// para retomar o fluxo após o navegador retornar do
// provedor OAuth.
//
// NÃO armazena:
// - AuthCredential;
// - tokens OAuth;
// - senha;
// - dados de sessão Firebase.
// =====================================================
interface SocialAuthRedirectContext {
  providerName: string;
  intent: SocialAuthIntent;
  mode: SocialAuthMode;
  createdAt: number;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private auth = inject(Auth);
  private firestore = inject(Firestore);
  private notificationService = inject(NotificationService);
  private router = inject(Router);
  private themeService = inject(ThemeService);

  private readonly socialRedirectContextKey =
    'movfascia:social-auth:redirect-context:v1';

  // =====================================================
  // VINCULAÇÃO SOCIAL PENDENTE
  // -----------------------------------------------------
  // Mantém temporariamente os dados necessários para
  // concluir com segurança a vinculação de um provedor
  // social a uma conta MOVFASCIA já existente.
  //
  // Estado somente em memória:
  // - não é persistido no Firestore;
  // - não é exposto diretamente aos componentes;
  // - é consumido somente após autenticação válida da
  //   conta MOVFASCIA existente.
  // =====================================================
  private pendingSocialCredential:
    AuthCredential | null = null;

  private pendingSocialProvider:
    string | null = null;

  private pendingSocialEmail:
    string | null = null;

  async waitForAuthReady(): Promise<User | null> {
    return new Promise<User | null>((resolve) => {
      // 1. Checagem imediata
      if (this.auth.currentUser) return resolve(this.auth.currentUser);

      const unsubscribe = this.auth.onAuthStateChanged((user) => {
        unsubscribe();
        resolve(user);
      });

      // Aumente para 10s no mobile ou use um retry logic
      setTimeout(() => {
        unsubscribe();
        resolve(this.auth.currentUser);
      }, 10000);
    });
  }

  // === FLAG PARA CONTROLAR FLUXO DE CADASTRO ===
  private isInSignupFlow = false;
  startSignupFlow() {
    this.isInSignupFlow = true;
    // console.log('AuthService: startSignupFlow() ativado');
  }
  endSignupFlow() {
    // Desativa com delay para garantir que o login automático já passou
    setTimeout(() => {
      this.isInSignupFlow = false;
      // console.log('AuthService: endSignupFlow() desativado');
    }, 500);
  }
  isSignupFlow(): boolean {
    return this.isInSignupFlow;
  }

  // =====================================================
  // FLAG: ABANDONO DE REGISTRO (evita conflito com guard)
  // =====================================================
  private isAbandoningRegistration = false;

  startAbandonRegistration() {
    this.isAbandoningRegistration = true;
    console.log('AuthService: startAbandonRegistration() ativado');
  }

  endAbandonRegistration() {
    this.isAbandoningRegistration = false;
    console.log('AuthService: endAbandonRegistration() desativado');
  }

  isAbandoning(): boolean {
    return this.isAbandoningRegistration;
  }

  // === FLAG PARA BYPASS TEMPORÁRIO DO AUTHGUARD DURANTE VERIFICAÇÃO DE E-MAIL ===
  private processingEmailVerification = false;
  setProcessingEmailVerification(active: boolean) {
    this.processingEmailVerification = active;
    // console.log('AuthService: isProcessingEmailVerification =', active);
  }

  // === CONSULTA DE ESTADO SOBERANO DURANTE VERIFICAÇÃO DE E-MAIL ===
  // Guards, componentes e serviços DEVEM usar este método
  // para evitar decisões de navegação durante processamento de token
  isProcessingEmailVerification(): boolean {
    return this.processingEmailVerification;
  }

  constructor() {
    console.log('AuthService: Inicializando');

    this.auth.onAuthStateChanged(
      (user) => {
        console.log(
          'AuthService.onAuthStateChanged: Estado de autenticação alterado:',
          user ? { uid: user.uid, email: user.email } : null
        );
      },
      (error: FirebaseError | undefined) => {
        console.error('AuthService.onAuthStateChanged: Erro no estado de autenticação:', error);
      }
    );
  }

  async signInWithCustomToken(token: string): Promise<void> {
    try {
      await signInWithCustomToken(this.auth, token);
      console.log('AuthService: signInWithCustomToken → login automático realizado com sucesso');
    } catch (error) {
      console.error('AuthService: Erro no signInWithCustomToken', error);
      throw error;
    }
  }

  async getCurrentUser(): Promise<User | null> {
    try {
      const user = await new Promise<User | null>((resolve) => {
        const unsubscribe = this.auth.onAuthStateChanged(
          (user) => {
            unsubscribe();
            resolve(user);
          },
          () => resolve(null)
        );
      });
      if (user) {
        await user.reload();
        await user.getIdToken(true);
        console.log('AuthService.getCurrentUser: Usuário atual:', {
          uid: user.uid,
          email: user.email,
          emailVerified: user.emailVerified,
        });
        return user;
      }
      return null;
    } catch (error) {
      console.error('AuthService.getCurrentUser: Erro ao obter usuário:', error);
      return null;
    }
  }

  /**
   * Consulta o usuário autenticado sem confundir ausência de sessão
   * com falhas de validação do Firebase Authentication.
   *
   * Não força a renovação do ID Token. O reload() atualiza os dados
   * do usuário, incluindo emailVerified.
   *
   * Mantém getCurrentUser() inalterado para preservar os
   * consumidores existentes.
   */
  async getCurrentUserResult(): Promise<CurrentUserResult> {
    try {
      const user = await this.waitForAuthReady();

      if (!user) {
        return {
          status: 'unauthenticated',
          user: null
        };
      }

      await user.reload();

      return {
        status: 'authenticated',
        user
      };

    } catch (error: unknown) {
      const firebaseError = error as FirebaseError;

      if (firebaseError?.code === 'auth/user-not-found') {
        return {
          status: 'unauthenticated',
          user: null
        };
      }

      console.error(
        'AuthService.getCurrentUserResult: Falha ao validar usuário:',
        error
      );

      return {
        status: 'error',
        user: null,
        error
      };
    }
  }

  async reauthenticate(password: string): Promise<void> {
    try {
      const user = await this.getCurrentUser();
      if (!user?.email) throw new Error('Usuário não autenticado');

      const credential = EmailAuthProvider.credential(user.email, password);
      await reauthenticateWithCredential(user, credential);

      console.log('AuthService.reauthenticate: Reautenticação bem-sucedida');
    } catch (error: unknown) {
      const firebaseError = error as FirebaseError;

      console.error(
        'AuthService.reauthenticate: Erro:',
        firebaseError
      );

      throw error;
    }
  }

  // === SIGNUP COM E-MAIL E SENHA ===
  async signUp(email: string, password: string): Promise<UserModel> {
    this.startSignupFlow();

    /**
     * Será preenchido somente depois que o Firebase Authentication
     * criar efetivamente o usuário.
     *
     * Se uma operação posterior falhar, esse usuário poderá ser
     * removido como rollback.
     */
    let createdAuthUser: User | null = null;

    const normalizedEmail = email.trim().toLowerCase();

    let signupStage:
      | 'starting'
      | 'auth-created'
      | 'firestore-created'
      = 'starting';

    try {
      console.log(
        'AuthService.signUp: Criando usuário com e-mail:',
        normalizedEmail
      );

      const userCredential = await createUserWithEmailAndPassword(
        this.auth,
        normalizedEmail,
        password
      );

      createdAuthUser = userCredential.user;
      signupStage = 'auth-created';

      const userData: UserModel = {
        uid: createdAuthUser.uid,
        email: createdAuthUser.email || normalizedEmail,
        displayName: createdAuthUser.displayName || '',

        createdAt: serverTimestamp() as Timestamp,
        updatedAt: serverTimestamp() as Timestamp,

        subscriptionStatus: 'inactive',
        hasSubscriptionHistory: false,

        userJourneyStatus: 'signup_started',
        lastStep: PROGRESS_STEPS.SIGNUP_STARTED,

        evaluationProgress: {
          questionnaireStep: 0,
          photoStep: 0,
          additionalQuestionStep: 0
        },

        trainingProgress: {
          currentWorkout: 1,
          currentExercise: 1
        }
      };

      await setDoc(
        doc(this.firestore, `users/${createdAuthUser.uid}`),
        userData
      );

      signupStage = 'firestore-created';

      console.log('AuthService.signUp: Dados salvos no Firestore:', {
        uid: userData.uid,
        email: userData.email,
        lastStep: userData.lastStep,
        userJourneyStatus: userData.userJourneyStatus
      });

      /**
       * createUserWithEmailAndPassword() já autentica automaticamente
       * o usuário recém-criado.
       *
       * Portanto, não é necessário executar novamente
       * signInWithEmailAndPassword().
       */
      return userData;

    } catch (error: unknown) {
      const firebaseError = error as FirebaseError;

      console.error('AuthService.signUp: Erro ao registrar:', {
        code: firebaseError.code,
        message: firebaseError.message,
        signupStage,
        authUserCreated: !!createdAuthUser,
        uid: createdAuthUser?.uid ?? null
      });

      /**
       * Executa rollback somente quando:
       *
       * 1. o usuário já foi criado no Authentication;
       * 2. o documento ainda não foi confirmado no Firestore.
       *
       * Isso evita excluir o Authentication caso uma futura operação
       * posterior ao setDoc() venha a falhar.
       */
      if (createdAuthUser && signupStage === 'auth-created') {
        try {
          await deleteUser(createdAuthUser);

          console.warn(
            'AuthService.signUp: Rollback concluído; usuário removido do Authentication:',
            {
              uid: createdAuthUser.uid,
              email: createdAuthUser.email
            }
          );

        } catch (rollbackError: unknown) {
          const rollbackFirebaseError = rollbackError as FirebaseError;

          /**
           * Não substituímos o erro original pelo erro de rollback.
           * O erro original continua sendo relançado ao componente.
           */
          console.error(
            'AuthService.signUp: Falha crítica ao executar rollback do Authentication:',
            {
              originalErrorCode: firebaseError.code,
              rollbackErrorCode: rollbackFirebaseError.code,
              rollbackErrorMessage: rollbackFirebaseError.message,
              uid: createdAuthUser.uid,
              email: createdAuthUser.email
            }
          );
        }
      }

      throw error;

    } finally {
      this.endSignupFlow();
    }
  }

  // === LOGIN COM E-MAIL E SENHA ===
  async signIn(email: string, password: string): Promise<UserModel> {
    try {
      console.log('AuthService.signIn: Fazendo login com e-mail:', email);
      const userCredential = await signInWithEmailAndPassword(this.auth, email, password);
      const user = userCredential.user;
      const userRef = doc(this.firestore, `users/${user.uid}`);

      const userDoc = await getDoc(userRef);

      // =====================================================
      // AUTH VÁLIDO SEM PERFIL MOVFASCIA
      // -----------------------------------------------------
      // Uma identidade existente no Firebase Authentication
      // não representa automaticamente uma conta MOVFASCIA.
      //
      // users/{uid} é obrigatório para liberar o aplicativo.
      // Contas administrativas, importadas ou legadas não
      // devem ser convertidas silenciosamente em clientes.
      // =====================================================
      if (!userDoc.exists()) {

        this.clearPendingSocialLink();

        await signOut(this.auth);

        throw new FirebaseAuthError(
          'Conta MOVFASCIA não encontrada.',
          'auth/movfascia-user-not-found'
        );
      }

      const userData = userDoc.data() as UserModel;
      // this.notificationService.presentToast('Login bem-sucedido!', 'success');
      return userData;
    } catch (error: unknown) {
      const firebaseError = error as FirebaseError;

      console.error(
        'AuthService.signIn: Erro ao autenticar:',
        {
          code: firebaseError.code,
          message: firebaseError.message
        }
      );

      // O AuthService não apresenta feedback visual.
      // A camada chamadora (SigninComponent) decide se deve:
      // - exibir toast;
      // - redirecionar;
      // - ou tratar silenciosamente o erro.
      throw error;
    }
  }

  // =====================================================
  // CONTEXTO DO SOCIAL AUTH VIA REDIRECT
  // =====================================================
  private saveSocialRedirectContext(
    providerName: string,
    intent: SocialAuthIntent,
    mode: SocialAuthMode
  ): void {

    if (typeof sessionStorage === 'undefined') {
      return;
    }

    const context: SocialAuthRedirectContext = {
      providerName,
      intent,
      mode,
      createdAt: Date.now()
    };

    sessionStorage.setItem(
      this.socialRedirectContextKey,
      JSON.stringify(context)
    );
  }

  private getSocialRedirectContext():
    SocialAuthRedirectContext | null {

    if (typeof sessionStorage === 'undefined') {
      return null;
    }

    const rawContext =
      sessionStorage.getItem(
        this.socialRedirectContextKey
      );

    if (!rawContext) {
      return null;
    }

    try {
      const parsed =
        JSON.parse(rawContext) as
          Partial<SocialAuthRedirectContext>;

      if (
        typeof parsed.providerName !== 'string' ||
        (
          parsed.intent !== 'signin' &&
          parsed.intent !== 'signup'
        ) ||
        (
          parsed.mode !== 'normal' &&
          parsed.mode !== 'resolve-link'
        ) ||
        typeof parsed.createdAt !== 'number'
      ) {
        this.clearSocialRedirectContext();
        return null;
      }

      const maxRedirectContextAgeMs =
        10 * 60 * 1000;

      if (
        Date.now() - parsed.createdAt >
        maxRedirectContextAgeMs
      ) {
        this.clearSocialRedirectContext();
        return null;
      }

      return {
        providerName: parsed.providerName,
        intent: parsed.intent,
        mode: parsed.mode,
        createdAt: parsed.createdAt
      };

    } catch {
      this.clearSocialRedirectContext();
      return null;
    }
  }

  private clearSocialRedirectContext(): void {

    if (typeof sessionStorage === 'undefined') {
      return;
    }

    sessionStorage.removeItem(
      this.socialRedirectContextKey
    );
  }


  // =====================================================
  // ESTRATÉGIA DE EXECUÇÃO DO SOCIAL AUTH
  // =====================================================
  private getSocialAuthExecutionStrategy():
    SocialAuthExecutionStrategy {

    // -----------------------------------------------------
    // 1. Aplicativo nativo Capacitor
    // -----------------------------------------------------
    if (Capacitor.isNativePlatform()) {
      return 'native';
    }

    // -----------------------------------------------------
    // 2. Ambiente Web
    // -----------------------------------------------------
    const userAgent =
      typeof navigator !== 'undefined'
        ? navigator.userAgent
        : '';

    const isMobileWeb =
      /Android|iPhone|iPad|iPod/i.test(userAgent);

    const isSafari =
      /Safari/i.test(userAgent) &&
      !/Chrome|CriOS|Chromium|Edg|OPR|FxiOS/i.test(userAgent);

    // Web mobile e Safari utilizarão redirect.
    if (isMobileWeb || isSafari) {
      return 'redirect';
    }

    // Web desktop permanece com popup.
    return 'popup';
  }

  // =====================================================
  // EXECUTOR DO SOCIAL AUTH
  // -----------------------------------------------------
  // Centraliza a escolha do mecanismo OAuth conforme o
  // ambiente detectado.
  //
  // Nesta etapa:
  // - popup já é a estratégia efetiva do Web desktop;
  // - redirect e native permanecem temporariamente usando
  //   popup para preservar o comportamento atual.
  //
  // A implementação efetiva dessas duas estratégias será
  // introduzida separadamente nas próximas etapas.
  // =====================================================
  private async executeSocialAuth(
    provider: AuthProvider,
    providerName: string,
    intent: SocialAuthIntent,
    mode: SocialAuthMode
  ): Promise<UserCredential> {

    const strategy =
      this.getSocialAuthExecutionStrategy();

    console.log(
      'AuthService.executeSocialAuth: Estratégia selecionada:',
      strategy
    );

    switch (strategy) {

      case 'popup':
        return signInWithPopup(
          this.auth,
          provider
        );

      case 'redirect':

        // =================================================
        // SOCIAL AUTH VIA REDIRECT
        // -------------------------------------------------
        // Antes de sair da aplicação, persistimos somente
        // o contexto não sensível necessário para retomar
        // o fluxo quando o Firebase retornar ao MOVFASCIA.
        //
        // A credencial OAuth NÃO é persistida aqui.
        // O UserCredential será recuperado posteriormente
        // através de getRedirectResult().
        // =================================================
        this.saveSocialRedirectContext(
          providerName,
          intent,
          mode
        );

        return signInWithRedirect(
          this.auth,
          provider
        );

      case 'native':
        // =================================================
        // COMPATIBILIDADE TEMPORÁRIA
        // -------------------------------------------------
        // Autenticação nativa Capacitor será implementada
        // posteriormente. Até lá, preservamos o mecanismo
        // já utilizado pelo projeto.
        // =================================================
        return signInWithPopup(
          this.auth,
          provider
        );
    }
  }

  // =====================================================
  // RETORNO DO SOCIAL AUTH VIA REDIRECT
  // -----------------------------------------------------
  // Recupera o resultado OAuth após o navegador retornar
  // ao MOVFASCIA e encaminha o UserCredential para o mesmo
  // processamento utilizado pelo fluxo popup.
  //
  // Retorna:
  // - UserModel: havia redirect válido e ele foi processado;
  // - null: esta inicialização não corresponde a um retorno
  //   social pendente.
  // =====================================================
  async consumeSocialRedirectResult():
    Promise<UserModel | null> {

    const context =
      this.getSocialRedirectContext();

    // Nenhum redirect iniciado pelo MOVFASCIA nesta aba.
    if (!context) {
      return null;
    }

    console.log(
      'AuthService.consumeSocialRedirectResult: ' +
      'Contexto de redirect encontrado.',
      {
        provider: context.providerName,
        intent: context.intent,
        mode: context.mode
      }
    );

    try {

      const userCredential =
        await getRedirectResult(this.auth);

      // =====================================================
      // CONTEXTO EXISTIA, MAS NÃO HÁ RESULTADO FIREBASE
      // -----------------------------------------------------
      // Pode representar fluxo interrompido/abandonado ou
      // contexto residual ainda válido.
      //
      // Não tentamos inferir autenticação.
      // =====================================================
      if (!userCredential) {

        this.clearSocialRedirectContext();

        console.warn(
          'AuthService.consumeSocialRedirectResult: ' +
          'Nenhum resultado de redirect foi encontrado.'
        );

        return null;
      }

      const userData =
        await this.processSocialUserCredential(
          userCredential,
          context.providerName,
          context.intent
        );

      // O redirect foi consumido com sucesso.
      this.clearSocialRedirectContext();

      console.log(
        'AuthService.consumeSocialRedirectResult: ' +
        'Redirect social processado com sucesso.',
        {
          uid: userCredential.user.uid,
          provider: context.providerName,
          intent: context.intent
        }
      );

      return userData;

    } catch (error: unknown) {

      // =====================================================
      // O RETORNO FOI CONSUMIDO
      // -----------------------------------------------------
      // Mesmo em erro, esta tentativa de redirect terminou.
      //
      // Se houver account-exists-with-different-credential,
      // handleSocialAuthError() converterá a credencial
      // recebida em pendingSocialCredential antes de lançar
      // o erro normalizado.
      // =====================================================
      this.clearSocialRedirectContext();

      this.handleSocialAuthError(
        error,
        context.providerName,
        context.mode
      );
    }
  }

  getPendingSocialRedirectIntent():
    SocialAuthIntent | null {

    const context =
      this.getSocialRedirectContext();

    return context?.intent ?? null;
  }

  getPendingSocialRedirectProvider():
    string | null {

    const context =
      this.getSocialRedirectContext();

    return context?.providerName ?? null;
  }

  // === LOGIN SOCIAL (Google / Facebook / Apple) ===
  async signInWithGoogle(
    intent: SocialAuthIntent,
    mode: SocialAuthMode = 'normal'
  ): Promise<UserModel> {
    return this.signInWithProvider(
      new GoogleAuthProvider(),
      'Google',
      intent,
      mode
    );
  }

  async signInWithFacebook(
    intent: SocialAuthIntent,
    mode: SocialAuthMode = 'normal'
  ): Promise<UserModel> {
    return this.signInWithProvider(
      new FacebookAuthProvider(),
      'Facebook',
      intent,
      mode
    );
  }

  async signInWithApple(
    intent: SocialAuthIntent,
    mode: SocialAuthMode = 'normal'
  ): Promise<UserModel> {
    const provider = new OAuthProvider('apple.com');
    provider.addScope('email');
    provider.addScope('name');

    return this.signInWithProvider(
      provider,
      'Apple',
      intent,
      mode
    );
  }

  private async signInWithProvider(
    provider: AuthProvider,
    providerName: string,
    intent: SocialAuthIntent,
    mode: SocialAuthMode
  ): Promise<UserModel> {
    try {

      // =====================================================
      // CICLO DE VIDA DA VINCULAÇÃO SOCIAL
      // -----------------------------------------------------
      // Login social normal inicia uma tentativa independente
      // e, portanto, encerra qualquer pendência anterior.
      //
      // No modo resolve-link, entretanto, o provider atual está
      // sendo utilizado para comprovar a conta MOVFASCIA já
      // existente. A credencial social originalmente pendente
      // deve permanecer intacta para ser vinculada depois.
      // =====================================================
      if (mode === 'normal') {
        this.clearPendingSocialLink();
      }

      // console.log(`AuthService.signInWith${providerName}: Iniciando login`);

      const userCredential =
        await this.executeSocialAuth(
          provider,
          providerName,
          intent,
          mode
        );

      return await this.processSocialUserCredential(
        userCredential,
        providerName,
        intent
      );
    } catch (error: unknown) {
      this.handleSocialAuthError(
        error,
        providerName,
        mode
      );
    }
  }

  private async processSocialUserCredential(
    userCredential: UserCredential,
    providerName: string,
    intent: SocialAuthIntent
  ): Promise<UserModel> {

    const user = userCredential.user;

    /**
     * Informa se esta autenticação criou uma identidade
     * nova no Firebase Authentication.
     *
     * Não confundir com a existência de users/{uid},
     * que representa o perfil MOVFASCIA.
     */
    const additionalUserInfo =
      getAdditionalUserInfo(userCredential);

    const isNewAuthUser =
      additionalUserInfo?.isNewUser === true;

    await user.reload();

    const userRef =
      doc(this.firestore, `users/${user.uid}`);

    const userDoc =
      await getDoc(userRef);

    const isNewMovfasciaUser =
      !userDoc.exists();

    // =====================================================
    // SIGNIN SEM PERFIL MOVFASCIA
    // -----------------------------------------------------
    // Uma identidade autenticada no Firebase não representa,
    // por si só, uma conta MOVFASCIA.
    //
    // Se a identidade Firebase tiver sido criada por esta
    // própria tentativa de Signin, executamos rollback.
    //
    // Se ela já existia anteriormente, apenas encerramos a
    // sessão e preservamos integralmente a identidade Auth.
    // =====================================================
    if (
      intent === 'signin' &&
      isNewMovfasciaUser
    ) {

      if (isNewAuthUser) {

        try {
          await deleteUser(user);

          console.warn(
            `AuthService.signInWith${providerName}: ` +
            'Signin criou identidade Auth sem perfil MOVFASCIA; rollback concluído.',
            {
              uid: user.uid,
              provider: providerName
            }
          );

        } catch (rollbackError: unknown) {
          const rollbackFirebaseError =
            rollbackError as FirebaseError;

          console.error(
            `AuthService.signInWith${providerName}: ` +
            'Falha ao remover identidade Auth criada durante Signin.',
            {
              code: rollbackFirebaseError.code,
              message: rollbackFirebaseError.message,
              uid: user.uid,
              provider: providerName
            }
          );

          /**
           * Mesmo que o rollback físico falhe, não podemos
           * manter uma sessão autenticada sem perfil MOVFASCIA.
           */
          try {
            await signOut(this.auth);
          } catch (signOutError) {
            console.error(
              `AuthService.signInWith${providerName}: ` +
              'Falha adicional ao encerrar sessão após rollback.',
              signOutError
            );
          }
        }

      } else {

        /**
         * Identidade Firebase preexistente.
         *
         * Pode representar administrador, usuário importado,
         * conta legada ou outra identidade legítima.
         *
         * Nunca deve ser excluída automaticamente.
         */
        await signOut(this.auth);
      }

      throw new FirebaseAuthError(
        'Conta MOVFASCIA não encontrada.',
        'auth/movfascia-user-not-found'
      );
    }

    const baseData: Partial<UserModel> = {
      uid: user.uid,
      email: user.email || '',
      displayName: user.displayName || '',
      updatedAt: serverTimestamp() as Timestamp,
    };

    const progressData: Partial<UserModel> =
      isNewMovfasciaUser
        ? {
            createdAt:
              serverTimestamp() as Timestamp,

            subscriptionStatus: 'inactive',
            hasSubscriptionHistory: false,

            userJourneyStatus: 'signup_started',
            lastStep: PROGRESS_STEPS.SIGNUP_STARTED,

            evaluationProgress: {
              questionnaireStep: 0,
              photoStep: 0,
              additionalQuestionStep: 0
            },

            trainingProgress: {
              currentWorkout: 1,
              currentExercise: 1
            }
          }
        : {};

    await setDoc(
      userRef,
      {
        ...baseData,
        ...progressData
      },
      { merge: true }
    );

    const persistedUserDoc =
      await getDoc(userRef);

    if (!persistedUserDoc.exists()) {
      throw new FirebaseAuthError(
        'Perfil MOVFASCIA não encontrado após autenticação social.',
        'auth/movfascia-user-not-found'
      );
    }

    return persistedUserDoc.data() as UserModel;
  }

  // =====================================================
  // TRATAMENTO COMUM DE ERROS DO SOCIAL AUTH
  // -----------------------------------------------------
  // Utilizado tanto pelo fluxo popup quanto pelo retorno
  // do fluxo redirect.
  //
  // Centraliza a normalização dos erros Firebase e evita
  // divergência entre os mecanismos OAuth.
  // =====================================================
  private handleSocialAuthError(
    error: unknown,
    providerName: string,
    mode: SocialAuthMode
  ): never {

    const firebaseError =
      error as FirebaseError;

    console.error(
      `AuthService.signInWith${providerName}: Erro:`,
      {
        code: firebaseError.code,
        message: firebaseError.message
      }
    );

    if (
      firebaseError.code ===
      'auth/account-exists-with-different-credential'
    ) {

      // =====================================================
      // CONFLITO DURANTE CONFIRMAÇÃO DA CONTA EXISTENTE
      // -----------------------------------------------------
      // Já existe uma vinculação social esperando conclusão.
      // Um novo conflito ocorrido durante a autenticação usada
      // para confirmar a conta não pode substituir a credencial
      // pendente original.
      // =====================================================
      if (mode === 'resolve-link') {
        throw new FirebaseAuthError(
          'O método informado não confirmou a conta MOVFASCIA existente.',
          'auth/movfascia-link-verification-provider-conflict'
        );
      }

      const pendingCredential =
        this.getCredentialFromProviderError(
          error,
          providerName
        );

      const pendingEmail =
        this.getEmailFromProviderError(error);

      if (pendingCredential) {
        this.pendingSocialCredential =
          pendingCredential;

        this.pendingSocialProvider =
          providerName;

        this.pendingSocialEmail =
          pendingEmail;

      } else {
        this.clearPendingSocialLink();
      }

      throw new FirebaseAuthError(
        'Já existe uma conta MOVFASCIA usando outro método de autenticação.',
        'auth/movfascia-provider-conflict'
      );
    }

    throw error;
  }

  // =====================================================
  // VINCULAÇÃO SOCIAL PENDENTE — API PÚBLICA
  // =====================================================
  hasPendingSocialLink(): boolean {
    return this.pendingSocialCredential !== null;
  }

  getPendingSocialProvider(): string | null {
    return this.pendingSocialProvider;
  }

  async completePendingSocialLink(): Promise<void> {
    const user = this.auth.currentUser;

    if (!user) {
      throw new FirebaseAuthError(
        'Usuário não autenticado.',
        'auth/movfascia-link-user-not-authenticated'
      );
    }

    const pendingCredential =
      this.pendingSocialCredential;

    if (!pendingCredential) {
      return;
    }

    // =====================================================
    // VALIDAÇÃO DA CONTA DESTINO
    // -----------------------------------------------------
    // Quando o Firebase informa o e-mail relacionado ao
    // conflito social, exigimos que a conta autenticada
    // corresponda ao mesmo endereço antes da vinculação.
    //
    // O UID continua sendo a identidade canônica do
    // MOVFASCIA; esta comparação é apenas uma proteção
    // adicional para o fluxo de resolução do conflito.
    // =====================================================
    const authenticatedEmail =
      user.email?.trim().toLowerCase() ?? null;

    if (
      this.pendingSocialEmail &&
      authenticatedEmail !== this.pendingSocialEmail
    ) {
      throw new FirebaseAuthError(
        'A conta autenticada não corresponde ao acesso social pendente.',
        'auth/movfascia-link-account-mismatch'
      );
    }

    try {
      const originalUid = user.uid;

      const result =
        await linkWithCredential(
          user,
          pendingCredential
        );

      if (result.user.uid !== originalUid) {
        throw new Error(
          'UID alterado inesperadamente durante vinculação de provedor.'
        );
      }

      console.log(
        'AuthService.completePendingSocialLink: ' +
        'Provedor vinculado com sucesso.',
        {
          uid: result.user.uid,
          provider:
            this.pendingSocialProvider
        }
      );

      this.clearPendingSocialLink();

    } catch (error: unknown) {
      const firebaseError =
        error as FirebaseError;

      console.error(
        'AuthService.completePendingSocialLink: ' +
        'Falha ao vincular provedor.',
        {
          code: firebaseError.code,
          message: firebaseError.message,
          provider:
            this.pendingSocialProvider
        }
      );

      // =====================================================
      // ERROS TERMINAIS DE VINCULAÇÃO
      // -----------------------------------------------------
      // - credential-already-in-use:
      //   a credencial pertence a outra identidade Firebase.
      //
      // - provider-already-linked:
      //   a conta destino já possui esse tipo de provedor.
      //
      // Repetir a mesma operação não resolve com segurança
      // nenhum desses estados. A tentativa pendente é
      // portanto encerrada.
      //
      // Não realizamos merge, exclusão de conta, substituição
      // de provedor, alteração de UID ou migração automática
      // de dados.
      // =====================================================
      if (
        firebaseError.code === 'auth/credential-already-in-use' ||
        firebaseError.code === 'auth/provider-already-linked'
      ) {
        this.clearPendingSocialLink();
      }

      throw error;
    }
  }

  // =====================================================
  // VINCULAÇÃO SOCIAL PENDENTE — MÉTODOS INTERNOS
  // =====================================================
  private clearPendingSocialLink(): void {
    this.pendingSocialCredential = null;
    this.pendingSocialProvider = null;
    this.pendingSocialEmail = null;
  }

  private getCredentialFromProviderError(
    error: unknown,
    providerName: string
  ): AuthCredential | null {

    switch (providerName) {

      case 'Google':
        return GoogleAuthProvider.credentialFromError(
          error as FirebaseError
        );

      case 'Facebook':
        return FacebookAuthProvider.credentialFromError(
          error as FirebaseError
        );

      case 'Apple':
        return OAuthProvider.credentialFromError(
          error as FirebaseError
        );

      default:
        return null;
    }
  }

  private getEmailFromProviderError(
    error: unknown
  ): string | null {

    const firebaseError = error as FirebaseError & {
      customData?: {
        email?: string | null;
      };
    };

    const email =
      firebaseError.customData?.email;

    return email
      ? email.trim().toLowerCase()
      : null;
  }

  /**
   * ===============================================================
   * VALIDAÇÃO DE CONSISTÊNCIA DA SESSÃO
   * ---------------------------------------------------------------
   * Fonte da verdade:
   * - Firebase Auth: sessão autenticada atual;
   * - Firestore users/{uid}: estado oficial do usuário no app.
   *
   * Objetivo:
   * detectar sessões antigas após alteração de e-mail confirmada
   * em outro navegador, aba, dispositivo ou futuro app nativo.
   */
  async validateCurrentSessionConsistency(): Promise<SessionConsistencyResult> {
    const user = await this.waitForAuthReady();

    if (!user) {
      return {
        isValid: false,
        reason: 'no-auth-user',
      };
    }

    await user.reload();
    await user.getIdToken(true);

    const authEmail = user.email || null;
    const userRef = doc(this.firestore, `users/${user.uid}`);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      return {
        isValid: false,
        reason: 'missing-firestore-user',
        uid: user.uid,
        oldEmail: authEmail,
        newEmail: null,
      };
    }

    const firestoreEmail = (userSnap.data()?.['email'] as string | undefined) || null;

    const normalizedAuthEmail = authEmail?.trim().toLowerCase() || null;
    const normalizedFirestoreEmail = firestoreEmail?.trim().toLowerCase() || null;

    if (
      normalizedAuthEmail &&
      normalizedFirestoreEmail &&
      normalizedAuthEmail !== normalizedFirestoreEmail
    ) {
      return {
        isValid: false,
        reason: 'email-changed',
        uid: user.uid,
        oldEmail: authEmail,
        newEmail: firestoreEmail,
      };
    }

    return {
      isValid: true,
      uid: user.uid,
    };
  }

  /**
   * ===============================================================
   * LOGOUT
   * ---------------------------------------------------------------
   * Executa apenas a invalidação de sessão (Firebase Auth).
   *
   * IMPORTANTE:
   * - NÃO realiza navegação
   * - NÃO manipula estado de rota
   *
   * A navegação deve ser responsabilidade do chamador (component),
   * garantindo previsibilidade e controle do fluxo.
   */
  async signOut(): Promise<void> {

    // =====================================================
    // ABANDONO DE VINCULAÇÃO SOCIAL
    // -----------------------------------------------------
    // Logout explícito encerra também qualquer tentativa
    // de vinculação social ainda pendente.
    //
    // A limpeza ocorre antes do signOut porque a intenção
    // do usuário de abandonar a sessão já foi expressa,
    // mesmo que o Firebase posteriormente reporte erro ao
    // encerrar a sessão.
    // =====================================================
    this.clearPendingSocialLink();
    this.clearSocialRedirectContext();

    try {
      // console.log('AuthService.signOut: Deslogando usuário');
      await signOut(this.auth);

      // Reseta o tema também no Logout, para que a tela de Signin volte ao padrão claro.
      this.themeService.resetToLightMode();

    } catch (error: unknown) {
      const firebaseError = error as FirebaseError;
      // console.error('AuthService.signOut: Erro:', firebaseError);
      this.notificationService.presentToast('Erro ao deslogar', 'danger');
      throw error;
    }
  }

  // === ESTADO DE AUTENTICAÇÃO — OBSERVÁVEL (COMPONENTES) ===
  getAuthState(): Observable<UserModel | null> {
    return authState(this.auth).pipe(

      switchMap((user) => {

        if (!user) return of(null);

        /**
         * ==========================================================
         * AJUSTE DEFINITIVO (SEM PATCH ESPALHADO)
         * ----------------------------------------------------------
         * Antes: getDoc() (snapshot único) → NÃO reage a updates
         *        do Firestore (ex.: ativação de assinatura).
         *
         * Agora: docData() (stream) → reage em tempo real às
         *        mudanças em users/{uid}, eliminando a necessidade
         *        de F5 para refletir subscriptionStatus / lastStep /
         *        userJourneyStatus no AppComponent/Profile.
         */
        const userRef = doc(this.firestore, `users/${user.uid}`);

        return docData(userRef).pipe(
          map((data) => {

            if (!data) return null;

            /*
             * =====================================================
             * FONTE DA VERDADE PARA emailVerified = Auth
             * -----------------------------------------------------
             * O Firestore não deve “mandar” neste campo, porque
             * emailVerified é propriedade do Firebase Auth.
             */
            return {
              ...(data as any),

              // GARANTE prioridade do Auth
              uid: user.uid,
              email: user.email || '',
              emailVerified: user.emailVerified,
            } as UserModel;
          })
        );
      }),

      // =====================================================
      // Evita "engolir" mudanças relevantes.
      // Antes você comparava uid + userJourneyStatus + emailVerified;
      // isso ainda pode perder updates de assinatura/etapas.
      // Mantemos mínimo para refletir progresso sem gerar spam.
      // =====================================================
      distinctUntilChanged((a, b) => {

        // null handling seguro
        if (!a || !b) return a === b;

        return (
          a.uid === b.uid &&
          a.userJourneyStatus === b.userJourneyStatus &&
          a.subscriptionStatus === b.subscriptionStatus &&
          a.lastStep === b.lastStep &&
          a.emailVerified === b.emailVerified
        );
      })
    )
  }
}
